import { Module } from "vuex";
import { RootState } from "../index";
import {
  getExperiments,
  createExperiments,
  editExperiments,
  toggleExperiment,
  getStatistics,
  resetStatistics,
  updateExperiments,
  deleteExperiments,
  getStatisticSignificance,
} from "@/shared/api/experiments";
import { createVariant } from "@/shared/api/variants";
import { ElMessage } from "element-plus";
import { IExperiment, IMatrixSignificance } from "@/shared/types/experiments";

export interface IState {
  list: IExperiment[];
  statistic: any[];
  significance: IMatrixSignificance;
}

const applicationsModule: Module<IState, RootState> = {
  namespaced: true,
  state: {
    list: [],
    statistic: [],
    significance: {},
  },
  mutations: {
    SET_LIST(state, list) {
      state.list = list;
    },
    ADD_ITEM_TO_LIST(state, item) {
      state.list.push(item);
    },
    DELETE_ITEM_FROM_LIST(state, id) {
      state.list = state.list.filter((item) => item.id !== id);
    },
    UPDATE_ITEM_IN_LIST(state, data) {
      state.list = state.list.map((item) => {
        if (item.id === data.id) {
          return { ...data };
        }
        return item;
      });
    },
    UPDATE_ITEM_VARIANT_IN_LIST(state, data) {
      const experiment = state.list.find((el) => el.id === data.id);
      if (experiment) {
        experiment.variants = data.variants;
      }
    },
    UPDATE_CUSTOM_STATUS(state, item) {
      const experiment = state.list.find((el) => el.id === item.id);
      if (experiment) {
        experiment.custom_status = item.custom_status;
      }
    },
    UPDATE_STATUS(state, item) {
      const experiment = state.list.find((el) => el.id === item.id);
      if (experiment) {
        experiment.status = item.status;
      }
    },
    SET_STATISTIC(state, list = []) {
      state.statistic = list;
    },
    SET_SIGNIFICANCE(state, list = {}) {
      state.significance = list;
    },
    RESET_STATISTIC(state, id) {
      state.statistic = state.statistic.map(() => ({}));
      state.significance = setPropertiesToNull(state.significance);
      const experiment = state.list.find((el) => el.id === id);
      if (experiment) {
        experiment.start_date = getTodayDate();
        experiment.end_date = null;
      }
    },
    DELETE_ITEM(state, id) {
      state.list = state.list.filter((item) => item.id !== id);
    },
  },
  actions: {
    async getExperiments({ commit }) {
      try {
        let allExperiments: IExperiment[] = [];

        // Load first page
        const firstResponse = await getExperiments();
        if (firstResponse && firstResponse.code === 200 && firstResponse.data) {
          allExperiments = [...firstResponse.data.data];

          // Check if there are more pages to load
          if (
            firstResponse.data.last_page > 1 &&
            firstResponse.data.next_page_url
          ) {
            let nextPageUrl: string | null = firstResponse.data.next_page_url;

            // Replace http: with https: if needed
            if (nextPageUrl && nextPageUrl.startsWith("http:")) {
              nextPageUrl = nextPageUrl.replace("http:", "https:");
            }

            // Load remaining pages
            while (nextPageUrl) {
              const pageResponse = await getExperiments(nextPageUrl);
              if (
                pageResponse &&
                pageResponse.code === 200 &&
                pageResponse.data
              ) {
                allExperiments = [...allExperiments, ...pageResponse.data.data];
                nextPageUrl = pageResponse.data.next_page_url || null;

                // Replace http: with https: if needed
                if (nextPageUrl && nextPageUrl.startsWith("http:")) {
                  nextPageUrl = nextPageUrl.replace("http:", "https:");
                }
              } else {
                break;
              }
            }
          }

          commit("SET_LIST", allExperiments);
          return { ...firstResponse.data, data: allExperiments };
        } else {
          throw firstResponse.message;
        }
      } catch (e: any) {
        ElMessage({
          message: e,
          type: "error",
        });
      }
    },
    async getStatistics(
      { commit },
      payload: {
        experiment_id: number;
        created_at: string;
        environment: string;
      }
    ) {
      try {
        const response = await getStatistics(payload);

        const list =
          response?.data && response?.data.length ? response?.data : [{}, {}];
        commit("SET_STATISTIC", list);
        return list;
      } catch (e: any) {
        ElMessage({
          message: e,
          type: "error",
        });
      }
    },
    async getStatisticSignificance({ commit }, payload) {
      try {
        const response = await getStatisticSignificance(payload);

        if (response && response.code === 200) {
          commit("SET_SIGNIFICANCE", response.data || {});
          return response.data;
        }
      } catch (e: any) {
        ElMessage({
          message: e,
          type: "error",
        });
      }
    },
    async deleteExperiments({ commit }, payload) {
      try {
        const response = await deleteExperiments(payload);

        if (response && response.code === 200 && response.data) {
          commit("DELETE_ITEM", payload);
          ElMessage({
            type: "success",
            message: "Experiment deleted successfully",
          });
        }
      } catch (e: any) {
        ElMessage({
          message: e,
          type: "error",
        });
      }
    },
    async createExperiments({ commit }, payload) {
      try {
        const res = await createExperiments(payload);
        if (res.code === 200) {
          commit("ADD_ITEM_TO_LIST", res.data);
          ElMessage.success("Test successfully created");
          return res.data;
        } else {
          if (res.code === 422) {
            ElMessage({
              message: res.message,
              type: "error",
            });
          } else {
            ElMessage.error("Something went wrong");
          }
        }
      } catch (e: any) {
        ElMessage({
          message: e,
          type: "error",
        });
      }
    },
    async editExperiments({ commit }, payload) {
      try {
        const res = await editExperiments(payload);
        if (res.code === 200) {
          commit("UPDATE_ITEM_IN_LIST", res.data);
          ElMessage({
            type: "success",
            message: "Test successfully edited",
          });
          return res.data;
        } else {
          if (res.code === 422) {
            ElMessage({
              message: res.message,
              type: "error",
            });
          } else {
            ElMessage.error("Something went wrong");
          }
        }
      } catch (e: any) {
        ElMessage({
          message: e,
          type: "error",
        });
      }
    },
    async createVariant({ commit }, payload) {
      try {
        const res = await createVariant(payload);

        if (res.code === 200) {
          commit("UPDATE_ITEM_VARIANT_IN_LIST", {
            id: payload.experiment_id,
            variants: res.data,
          });
          return Promise.resolve(res);
        } else {
          if (Array.isArray(res.data)) {
            res.data.forEach((error) => {
              ElMessage({
                message: error,
                type: "error",
              });
            });
          } else {
            ElMessage({
              message: res.message,
              type: "error",
            });
          }
        }
      } catch (e: any) {
        ElMessage({
          message: e,
          type: "error",
        });
      }
    },
    async toggleExperiment({ commit }, payload) {
      try {
        const res = await toggleExperiment(payload);
        if (res.code === 200) {
          commit("UPDATE_STATUS", res.data);

          if (res.data.status === "draft" || res.data.status === "stopped") {
            ElMessage({
              type: "error",
              message: "Test stopped",
            });
          } else if (res.data.status === "active") {
            ElMessage({
              type: "success",
              message: "Test has been runned",
            });
          }
          return res.data;
        } else {
          ElMessage.error("Something went wrong");
        }
      } catch (e: any) {
        ElMessage({
          message: e,
          type: "error",
        });
      }
    },
    async resetStatistics({ commit }, payload) {
      try {
        const res = await resetStatistics(payload);
        if (res.code === 200) {
          commit("RESET_STATISTIC", payload);

          ElMessage({
            type: "success",
            message: "Statistics reset successfully",
          });
          return res.data;
        } else {
          ElMessage.error("Something went wrong");
        }
      } catch (e: any) {
        ElMessage({
          message: e,
          type: "error",
        });
      }
    },
    async updateExperiments({ commit }, payload) {
      try {
        const res = await updateExperiments(payload);
        if (res.code === 200) {
          commit("UPDATE_CUSTOM_STATUS", res.data);
          ElMessage({
            type: "success",
            message: "Status update successfully",
          });
        } else {
          if (res.code === 422) {
            ElMessage({
              message: res.message,
              type: "error",
            });
          } else {
            ElMessage.error("Something went wrong");
          }
        }
      } catch (e: any) {
        console.error(e);
      }
    },
  },
};

function getTodayDate() {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, "0");
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const year = today.getFullYear();

  const formattedDate = `${day}.${month}.${year}`;
  return formattedDate;
}

function setPropertiesToNull(obj: any) {
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      if (typeof obj[key] === "object" && obj[key] !== null) {
        setPropertiesToNull(obj[key]);
      } else {
        obj[key] = null;
      }
    }
  }
  return obj;
}

export default applicationsModule;
